import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/icons';
import { AsyncPanel } from '@/components/platform/AsyncPanel';
import { Pagination, SortableHeader } from '@/components/platform/Pagination';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { platformApi } from '@/api/platform';
import { InstitutionPicker } from '@/components/platform/InstitutionPicker';
import { useAsyncData } from '@/hooks';
import {
  AUDIT_OUTCOMES,
  AUDIT_VIEWS,
  DEFAULT_PAGE_SIZE,
  PRIVILEGED_ACTION_TYPES,
  type AuditEvent,
  type AuditOutcome,
  type AuditView,
  type Page,
  type SortDirection,
} from '@/types/platform';
import { formatDateTime } from '@/utils/format';
import styles from './platform-pages.module.css';

/**
 * PA-14 — the governance surface.
 *
 * An audit trail nobody can search is not an audit trail. Every design decision on
 * this screen follows from that one sentence:
 *
 *  - The dedicated views (denials, cross-tenant denials, role elevations,
 *    suspensions, product and mapping changes) are separate SERVER routes, not a
 *    client-side filter over one list. The server owns which event types make up
 *    "a role elevation"; approximating it here produces a report that omits
 *    whatever was added since this build shipped, and an under-reporting audit
 *    report reads exactly like a clean bill of health.
 *  - Filtering, paging and sorting are all requests. A filter applied to the rows
 *    already on screen answers "how many of these twenty-five" when the question
 *    was "how many are there".
 *  - Before and after values are whole snapshots, so the comparison is computed at
 *    read time and the screen says which keys exist on only one side rather than
 *    quietly showing one column.
 *  - Export writes what is on screen, and says so. An export button that silently
 *    exports one page while the operator believes they have the whole result set
 *    is how an incomplete evidence pack reaches a regulator.
 */

const OUTCOME_TONES: Record<AuditOutcome, string> = {
  SUCCESS: 'okTag',
  FAILURE: 'warnTag',
  DENIED: 'dangerTag',
  ERROR: 'dangerTag',
};

interface AuditFilterState {
  actorKeycloakUserId: string;
  actorPersonId: string;
  institutionId: string;
  eventType: string;
  outcome: AuditOutcome | 'ALL';
  from: string;
  to: string;
  correlationId: string;
}

const EMPTY_FILTERS: AuditFilterState = {
  actorKeycloakUserId: '',
  actorPersonId: '',
  institutionId: '',
  eventType: '',
  outcome: 'ALL',
  from: '',
  to: '',
  correlationId: '',
};

export function AuditPage() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;

  const [view, setView] = useState<AuditView>('EVENTS');
  const [draft, setDraft] = useState<AuditFilterState>(EMPTY_FILTERS);
  const [applied, setApplied] = useState<AuditFilterState>(EMPTY_FILTERS);
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState<{ field: string; order: SortDirection }>({
    field: 'occurredAt',
    order: 'DESC',
  });
  const [inspecting, setInspecting] = useState<AuditEvent | null>(null);

  const key = `audit:${view}:${page}:${sort.field}:${sort.order}:${JSON.stringify(applied)}`;
  const fetcher = useCallback(
    () =>
      platformApi.audit.search(view, {
        actorKeycloakUserId: applied.actorKeycloakUserId || undefined,
        actorPersonId: applied.actorPersonId || undefined,
        institutionId: applied.institutionId || undefined,
        eventTypes: applied.eventType ? [applied.eventType] : undefined,
        outcomes: applied.outcome === 'ALL' ? undefined : [applied.outcome],
        // A date alone is midnight; the "to" bound is pushed to the end of the day
        // so that "1st to 1st" means that day rather than an empty range.
        from: applied.from ? `${applied.from}T00:00:00.000Z` : undefined,
        to: applied.to ? `${applied.to}T23:59:59.999Z` : undefined,
        correlationId: applied.correlationId || undefined,
        page,
        limit: DEFAULT_PAGE_SIZE,
        sort: sort.field,
        order: sort.order,
      }),
    [view, applied, page, sort],
  );

  const { state, reload } = useAsyncData<Page<AuditEvent>>(key, fetcher);

  const rows = state.status === 'ready' ? state.data.data : [];
  const filtersActive = useMemo(
    () => Object.entries(applied).some(([field, value]) => value !== EMPTY_FILTERS[field as keyof AuditFilterState]),
    [applied],
  );

  const apply = (event: React.FormEvent) => {
    event.preventDefault();
    setPage(1);
    setApplied(draft);
  };

  const changeView = (next: AuditView) => {
    setView(next);
    setPage(1);
  };

  return (
    <div className={styles.page}>
      <header className={styles.pageHeader}>
        <div>
          <h1 className={styles.pageTitle}>{t('platform.audit.title')}</h1>
          <p className={styles.pageLead}>{t('platform.audit.lead')}</p>
        </div>
        <button
          type="button"
          className={styles.secondaryBtn}
          onClick={() => downloadAuditCsv(rows, view, locale)}
          disabled={rows.length === 0}
        >
          <Icon name="external" size={15} />
          {t('platform.audit.export')}
        </button>
      </header>

      {/* The tab list is the primary control, so it sits above the filters: which
          question is being asked comes before how it is narrowed. */}
      <div className={styles.tabBar} role="tablist" aria-label={t('platform.audit.viewsLabel')}>
        {AUDIT_VIEWS.map((candidate) => (
          <button
            key={candidate}
            type="button"
            role="tab"
            id={`audit-tab-${candidate}`}
            aria-selected={view === candidate}
            aria-controls="audit-results"
            className={`${styles.tab} ${view === candidate ? styles.tabActive : ''}`}
            onClick={() => changeView(candidate)}
          >
            {t(`platform.audit.view.${candidate}`)}
          </button>
        ))}
      </div>

      <p className={styles.dataNotice}>
        <Icon name="info" size={15} aria-hidden="true" />
        {t(`platform.audit.viewNote.${view}`)}
      </p>

      <form className={styles.filterGrid} onSubmit={apply} role="search">
        <div className={styles.filterField}>
          <label className={styles.filterLabel} htmlFor="audit-actor">
            {t('platform.audit.filterActor')}
          </label>
          <input
            id="audit-actor"
            className={styles.input}
            value={draft.actorKeycloakUserId}
            placeholder={t('platform.audit.filterActorPlaceholder')}
            aria-describedby="audit-actor-hint"
            onChange={(event) => setDraft({ ...draft, actorKeycloakUserId: event.target.value })}
          />
          <span className={styles.fieldHint} id="audit-actor-hint">
            {t('platform.audit.filterActorHint')}
          </span>
        </div>

        <div className={styles.filterField}>
          <label className={styles.filterLabel} htmlFor="audit-institution">
            {t('platform.audit.filterInstitution')}
          </label>
          <InstitutionPicker
            id="audit-institution"
            value={draft.institutionId}
            clearable
            onChange={(next) => setDraft({ ...draft, institutionId: next })}
          />
        </div>

        <div className={styles.filterField}>
          <label className={styles.filterLabel} htmlFor="audit-action">
            {t('platform.audit.filterAction')}
          </label>
          <Select
            id="audit-action"
            searchable
            value={draft.eventType}
            onChange={(value) => setDraft({ ...draft, eventType: value })}
            placeholder={t('platform.audit.filterActionAll')}
            options={PRIVILEGED_ACTION_TYPES.map((action) => ({
              value: action,
              label: t(`platform.audit.action.${action}`, { defaultValue: action }),
            }))}
          />
        </div>

        <div className={styles.filterField}>
          <label className={styles.filterLabel} htmlFor="audit-outcome">
            {t('platform.audit.filterOutcome')}
          </label>
          <Select
            id="audit-outcome"
            value={draft.outcome}
            onChange={(value) => setDraft({ ...draft, outcome: value as AuditOutcome | 'ALL' })}
            options={[
              { value: 'ALL', label: t('platform.audit.filterOutcomeAll') },
              ...AUDIT_OUTCOMES.map((outcome) => ({
                value: outcome,
                label: t(`platform.audit.outcome.${outcome}`),
              })),
            ]}
          />
        </div>

        <div className={styles.filterField}>
          <label className={styles.filterLabel} htmlFor="audit-from">
            {t('platform.audit.filterFrom')}
          </label>
          <input
            id="audit-from"
            type="date"
            className={styles.input}
            value={draft.from}
            max={draft.to || undefined}
            onChange={(event) => setDraft({ ...draft, from: event.target.value })}
          />
        </div>

        <div className={styles.filterField}>
          <label className={styles.filterLabel} htmlFor="audit-to">
            {t('platform.audit.filterTo')}
          </label>
          <input
            id="audit-to"
            type="date"
            className={styles.input}
            value={draft.to}
            min={draft.from || undefined}
            onChange={(event) => setDraft({ ...draft, to: event.target.value })}
          />
        </div>

        <div className={styles.filterField}>
          <label className={styles.filterLabel} htmlFor="audit-correlation">
            {t('platform.audit.filterCorrelation')}
          </label>
          <input
            id="audit-correlation"
            className={styles.input}
            value={draft.correlationId}
            onChange={(event) => setDraft({ ...draft, correlationId: event.target.value })}
          />
        </div>

        <div className={styles.filterActions}>
          <button type="submit" className={styles.primaryBtnSm}>
            <Icon name="filter" size={15} />
            {t('platform.audit.apply')}
          </button>
          <button
            type="button"
            className={styles.secondaryBtn}
            onClick={() => {
              setDraft(EMPTY_FILTERS);
              setApplied(EMPTY_FILTERS);
              setPage(1);
            }}
            disabled={!filtersActive && draft === EMPTY_FILTERS}
          >
            {t('platform.audit.clear')}
          </button>
          <button type="button" className={styles.secondaryBtn} onClick={reload}>
            <Icon name="refresh" size={15} />
            {t('platform.dashboard.refresh')}
          </button>
        </div>
      </form>

      <div id="audit-results" role="tabpanel" aria-labelledby={`audit-tab-${view}`}>
        <AsyncPanel
          state={state}
          onRetry={reload}
          isEmpty={(data) => data.data.length === 0}
          emptyTitle={t('platform.audit.emptyTitle')}
          emptyBody={filtersActive ? t('platform.audit.emptyFiltered') : t('platform.audit.emptyBody')}
        >
          {(data) => (
            <>
              <div className={styles.tableWrap}>
                <table className={styles.table}>
                  <caption className={styles.tableCaption}>
                    {t('platform.audit.tableCaption', { view: t(`platform.audit.view.${view}`), n: data.total })}
                  </caption>
                  <thead>
                    <tr>
                      <SortableHeader
                        field="occurredAt"
                        label={t('platform.audit.colWhen')}
                        activeField={sort.field}
                        direction={sort.order}
                        onSort={(field, order) => {
                          setSort({ field, order });
                          setPage(1);
                        }}
                      />
                      <SortableHeader
                        field="eventType"
                        label={t('platform.audit.colAction')}
                        activeField={sort.field}
                        direction={sort.order}
                        onSort={(field, order) => {
                          setSort({ field, order });
                          setPage(1);
                        }}
                      />
                      <th scope="col">{t('platform.audit.colActor')}</th>
                      <th scope="col">{t('platform.audit.colTarget')}</th>
                      <SortableHeader
                        field="outcome"
                        label={t('platform.audit.colOutcome')}
                        activeField={sort.field}
                        direction={sort.order}
                        onSort={(field, order) => {
                          setSort({ field, order });
                          setPage(1);
                        }}
                      />
                      <th scope="col">
                        <span className={styles.srOnlyText}>{t('platform.audit.colInspect')}</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.data.map((entry) => (
                      <tr key={entry.id}>
                        <th scope="row" className={styles.tableRowHeader}>
                          <span>{formatDateTime(entry.occurredAt, locale)}</span>
                          <span className={styles.tableSub}>{entry.correlationId}</span>
                        </th>
                        <td>
                          {t(`platform.audit.action.${entry.eventType}`, { defaultValue: entry.eventType })}
                        </td>
                        <td>
                          <span>{entry.actorName ?? entry.actorPersonId ?? entry.actorKeycloakUserId ?? '—'}</span>
                          {entry.sourceIp && <span className={styles.tableSub}>{entry.sourceIp}</span>}
                        </td>
                        <td>
                          <span>{entry.targetType}</span>
                          {entry.targetId && <span className={styles.tableSub}>{entry.targetId}</span>}
                        </td>
                        <td>
                          <span className={styles[OUTCOME_TONES[entry.outcome] ?? 'okTag']}>
                            {t(`platform.audit.outcome.${entry.outcome}`, { defaultValue: entry.outcome })}
                          </span>
                        </td>
                        <td>
                          <button type="button" className={styles.linkBtn} onClick={() => setInspecting(entry)}>
                            {t('platform.audit.inspect')}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <Pagination
                page={data.page}
                limit={data.limit}
                total={data.total}
                totalPages={data.totalPages}
                onPage={setPage}
                label={t('platform.audit.title')}
              />
            </>
          )}
        </AsyncPanel>
      </div>

      <AuditDetailDialog entry={inspecting} onClose={() => setInspecting(null)} locale={locale} />
    </div>
  );
}

/* ─── Detail ─── */

function AuditDetailDialog({
  entry,
  onClose,
  locale,
}: {
  entry: AuditEvent | null;
  onClose: () => void;
  locale: string;
}) {
  const { t } = useTranslation();
  if (!entry) return null;

  return (
    <Modal
      open
      onClose={onClose}
      title={t(`platform.audit.action.${entry.eventType}`, { defaultValue: entry.eventType })}
      description={t('platform.audit.detailLead', {
        when: formatDateTime(entry.occurredAt, locale),
        actor: entry.actorName ?? entry.actorPersonId ?? entry.actorKeycloakUserId ?? t('platform.audit.unknownActor'),
      })}
    >
      <div className={styles.auditDetail}>
        <dl className={styles.detailGrid}>
          <dt>{t('platform.audit.colOutcome')}</dt>
          <dd>{t(`platform.audit.outcome.${entry.outcome}`, { defaultValue: entry.outcome })}</dd>
          <dt>{t('platform.audit.colTarget')}</dt>
          <dd>
            {entry.targetType}
            {entry.targetId ? ` · ${entry.targetId}` : ''}
          </dd>
          <dt>{t('platform.audit.institution')}</dt>
          <dd>{entry.institutionName ?? entry.institutionId ?? '—'}</dd>
          <dt>{t('platform.audit.correlation')}</dt>
          <dd>{entry.correlationId}</dd>
          <dt>{t('platform.audit.source')}</dt>
          <dd>{entry.sourceIp ?? '—'}</dd>
        </dl>

        {entry.reason && (
          <section>
            <h3 className={styles.subheading}>{t('platform.audit.reason')}</h3>
            <p className={styles.auditReason}>{entry.reason}</p>
          </section>
        )}

        <ChangeInspector before={entry.beforeValue} after={entry.afterValue} />
        <ApprovalChain entries={entry.approvalChain} locale={locale} />
      </div>

      <div className="modal-actions">
        <button type="button" className="modal-button" onClick={onClose}>
          {t('common.close')}
        </button>
      </div>
    </Modal>
  );
}

/**
 * Before and after, paired key by key.
 *
 * The two snapshots are not guaranteed to have the same shape — they were written
 * by whichever service performed the action, possibly several schema versions ago.
 * So the union of the keys is walked, an absent side is labelled as absent rather
 * than rendered blank, and unchanged keys are marked so the eye goes to what moved.
 */
function ChangeInspector({
  before,
  after,
}: {
  before?: Record<string, unknown>;
  after?: Record<string, unknown>;
}) {
  const { t } = useTranslation();

  const keys = useMemo(
    () => [...new Set([...Object.keys(before ?? {}), ...Object.keys(after ?? {})])].sort(),
    [before, after],
  );

  if (!before && !after) {
    return (
      <section>
        <h3 className={styles.subheading}>{t('platform.audit.changes')}</h3>
        <p className={styles.sectionEmpty} role="status">
          {t('platform.audit.noSnapshots')}
        </p>
      </section>
    );
  }

  return (
    <section>
      <h3 className={styles.subheading}>{t('platform.audit.changes')}</h3>
      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <caption className={styles.tableCaption}>{t('platform.audit.changesCaption')}</caption>
          <thead>
            <tr>
              <th scope="col">{t('platform.audit.field')}</th>
              <th scope="col">{t('platform.audit.before')}</th>
              <th scope="col">{t('platform.audit.after')}</th>
            </tr>
          </thead>
          <tbody>
            {keys.map((key) => {
              const hadBefore = before !== undefined && key in before;
              const hasAfter = after !== undefined && key in after;
              const beforeText = hadBefore ? renderValue(before[key]) : null;
              const afterText = hasAfter ? renderValue(after[key]) : null;
              const changed = beforeText !== afterText;
              return (
                <tr key={key} className={changed ? styles.changedRow : undefined}>
                  <th scope="row" className={styles.tableRowHeader}>
                    {key}
                    {!changed && <span className={styles.tableSub}>{t('platform.audit.unchanged')}</span>}
                  </th>
                  <td className={hadBefore ? undefined : styles.mutedCell}>
                    {hadBefore ? beforeText : t('platform.audit.absent')}
                  </td>
                  <td className={hasAfter ? undefined : styles.mutedCell}>
                    {hasAfter ? afterText : t('platform.audit.absent')}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function ApprovalChain({ entries, locale }: { entries?: import('@/types/platform').ApprovalChainEntry[]; locale: string }) {
  const { t } = useTranslation();

  return (
    <section>
      <h3 className={styles.subheading}>{t('platform.audit.approvalChain')}</h3>
      {!entries || entries.length === 0 ? (
        <p className={styles.sectionEmpty} role="status">
          {t('platform.audit.noApprovalChain')}
        </p>
      ) : (
        <ol className={styles.chain}>
          {entries.map((step, index) => (
            <li key={`${step.step}-${step.actorId}-${index}`} className={styles.chainItem}>
              <span className={styles.chainDecision}>
                {t(`platform.audit.decision.${step.decision}`, { defaultValue: step.decision })}
              </span>
              <span className={styles.chainBody}>
                <strong>{step.step}</strong> · {step.actorId}
                <span className={styles.tableSub}>{formatDateTime(step.occurredAt, locale)}</span>
                {step.note && <span className={styles.tableSub}>{step.note}</span>}
              </span>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}

function renderValue(value: unknown): string {
  if (value === null) return 'null';
  if (value === undefined) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  try {
    return JSON.stringify(value);
  } catch {
    return '[unserialisable]';
  }
}

/* ─── Export ─── */

const CSV_COLUMNS = [
  'occurredAt',
  'eventType',
  'outcome',
  'actor',
  'targetType',
  'targetId',
  'institutionId',
  'correlationId',
  'sourceIp',
  'reason',
] as const;

/**
 * Exports the rows currently on screen.
 *
 * Deliberately NOT a silent "export everything": the server has no export route
 * yet, and quietly writing one page while the operator believes they have the
 * whole result set is how an incomplete evidence pack ends up attached to a
 * regulatory response. The filename carries the view and the row count so the file
 * says what it is once it has left this screen.
 */
function downloadAuditCsv(rows: readonly AuditEvent[], view: AuditView, locale: string): void {
  if (rows.length === 0) return;

  const header = CSV_COLUMNS.join(',');
  const lines = rows.map((entry) =>
    CSV_COLUMNS.map((column) => {
      const value =
        column === 'actor'
          ? (entry.actorName ?? entry.actorPersonId ?? entry.actorKeycloakUserId ?? '')
          : (entry[column as keyof AuditEvent] ?? '');
      return escapeCsv(typeof value === 'string' ? value : renderValue(value));
    }).join(','),
  );

  const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
  const blob = new Blob([`${header}\n${lines.join('\n')}\n`], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `campusone-audit-${view.toLowerCase()}-${rows.length}-rows-${stamp}-${locale}.csv`;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

/**
 * RFC 4180 quoting, plus a guard against spreadsheet formula injection.
 *
 * An audit reason beginning `=` is executed by Excel when the file is opened. The
 * reason field is free text written by whoever performed the action, which makes
 * it attacker-controlled in exactly the export a security team is most likely to
 * open.
 */
function escapeCsv(value: string): string {
  const guarded = /^[=+\-@\t\r]/.test(value) ? `'${value}` : value;
  return `"${guarded.replace(/"/g, '""')}"`;
}
